import { useState } from "react";
import { CreateEntityButton } from "./CreateEntityButton";
import { DialogActions, Modal } from "./Modal";
import { Field, Select, TextArea, TextInput } from "./Field";
import { HEALTH_OPTIONS } from "./HealthDot";
import { useRoster } from "../data/RosterContext";
import { OwnerPicker } from "./OwnerPicker";
import type { AccountHealth, CreateAccountInput } from "../data/types";

/**
 * "Add account" — button, dialog and confirmation in one, mirroring
 * NewRequestButton. Persistence is the caller's job via onCreated (a POST +
 * revalidate on the Accounts screen), so the dialog stays pure and testable.
 */
export function AddAccountButton({ onCreated }: { onCreated?: (input: CreateAccountInput) => void }) {
  return (
    <CreateEntityButton<CreateAccountInput>
      label="Add account"
      buildConfirmation={(input) => `${input.name} added`}
      onResult={onCreated}
    >
      {(close, create) => <AddAccountDialog onClose={close} onCreate={create} />}
    </CreateEntityButton>
  );
}

function AddAccountDialog({ onClose, onCreate }: { onClose: () => void; onCreate: (i: CreateAccountInput) => void }) {
  const { members } = useRoster();
  const [name, setName] = useState("");
  const [emailDomain, setEmailDomain] = useState("");
  const [plan, setPlan] = useState<"Growth" | "Enterprise">("Growth");
  const [health, setHealth] = useState<AccountHealth>("healthy");
  const [leadOwnerId, setLeadOwnerId] = useState<string>("lucas");
  const [brief, setBrief] = useState("");
  const [touched, setTouched] = useState(false);

  const nameMissing = name.trim() === "";
  const domainMissing = emailDomain.trim() === "";

  function submit() {
    if (nameMissing || domainMissing) {
      setTouched(true);
      return;
    }
    onCreate({ name: name.trim(), emailDomain: emailDomain.trim(), plan, health, leadOwnerId, brief: brief.trim() });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Add account"
      footer={<DialogActions onCancel={onClose} onConfirm={submit} confirmLabel="Add account" />}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" htmlFor="aa-name" span2>
          <TextInput
            id="aa-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched(true)}
            aria-invalid={touched && nameMissing}
            placeholder="Company name"
          />
          {touched && nameMissing && <span className="text-xs text-error-600">A name is required.</span>}
        </Field>

        <Field label="Email domain" htmlFor="aa-domain">
          <TextInput
            id="aa-domain"
            value={emailDomain}
            onChange={(e) => setEmailDomain(e.target.value)}
            aria-invalid={touched && domainMissing}
            placeholder="acme.com"
          />
          {touched && domainMissing && <span className="text-xs text-error-600">A domain is required.</span>}
        </Field>

        <Field label="Plan" htmlFor="aa-plan">
          <Select id="aa-plan" value={plan} onChange={(e) => setPlan(e.target.value as "Growth" | "Enterprise")}>
            <option>Growth</option>
            <option>Enterprise</option>
          </Select>
        </Field>

        <Field label="Health" htmlFor="aa-health">
          <Select id="aa-health" value={health} onChange={(e) => setHealth(e.target.value as AccountHealth)}>
            {HEALTH_OPTIONS.map((h) => (
              <option key={h.value} value={h.value}>
                {h.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="CE lead" htmlFor="aa-lead">
          <OwnerPicker id="aa-lead" ariaLabel="CE lead" members={members} value={leadOwnerId} onChange={setLeadOwnerId} />
        </Field>

        <Field label="Notes" htmlFor="aa-brief" hint="(optional)" span2>
          <TextArea
            id="aa-brief"
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            placeholder="Anything worth knowing before the next conversation"
          />
        </Field>
      </div>
    </Modal>
  );
}
